import React, { useState, useEffect } from 'react';
import { FiSearch, FiXCircle, FiEye, FiMail, FiList } from 'react-icons/fi';
import PdfOutlineModal from '../components/PdfOutlineModal';

const SearchView = ({
  query,
  setQuery,
  handleSearch,
  clearSearch,
  results,
  hasSearched,
  selectedRow,
  setSelectedRow,
  parseFilename,
  fieldLabels = [],
  fieldKeys = [],
  API_URL,
  onEmail
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [outlineFile, setOutlineFile] = useState('');
  // Pagination: rows per page persisted, current page in-memory
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('search.rowsPerPage') || '50', 10);
      return Number.isFinite(v) && v > 0 ? v : 50;
    } catch (_) { return 50; }
  });
  const [page, setPage] = useState(0);

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const getSortedResults = () => {
    const sorted = [...results];
    if (!sortField) return sorted;

    sorted.sort((a, b) => {
      const aVal = String(parseFilename(a.filename)[sortField] || '').toLowerCase();
      const bVal = String(parseFilename(b.filename)[sortField] || '').toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedResults = getSortedResults();

  // Persist rows-per-page
  useEffect(() => {
    try { localStorage.setItem('search.rowsPerPage', String(rowsPerPage)); } catch (_) {}
  }, [rowsPerPage]);

  // Clamp/reset page on changes
  const totalPages = Math.max(1, Math.ceil(sortedResults.length / Math.max(1, rowsPerPage)));
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, sortField, sortDirection, sortedResults.length]);

  const startIdx = page * rowsPerPage;
  const endIdx = Math.min(sortedResults.length, startIdx + rowsPerPage);
  const pageResults = sortedResults.slice(startIdx, endIdx);

  return (
    <div>
      <p style={{ margin: '0 0 10px', color: 'var(--search-hint-color)', fontSize: 14 }}>
        Search your PDFs by typing any word or phrase. You can also enter a
        File #, Buyer name, or Seller name to quickly find related documents.<br />
        Try examples like a street address (e.g., "123 Main St"), a city name
        (e.g., "Miami"), or key document names such as "settlement statement"
        or "mortgage".
      </p>
      <input
        style={{
          padding: '8px 12px',
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text)',
          border: '1px solid var(--border-soft)',
          borderRadius: 6,
          width: '100%',
          boxSizing: 'border-box'
        }}
        type="text"
        placeholder="Search by word, phrase, File #, Buyer, Seller, address (e.g., 123 Main St), city (e.g., Miami), or doc name (e.g., settlement statement, mortgage)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSearch();
        }}
      />
      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={handleSearch}>
          <FiSearch />
          <span style={{ marginLeft: 6 }}>Search</span>
        </button>
        <button className="btn btn-clear" onClick={clearSearch}>
          <FiXCircle />
          <span style={{ marginLeft: 6 }}>Clear</span>
        </button>
      </div>

      {hasSearched && sortedResults.length === 0 && (
        <p style={{ marginTop: 20, color: '#666' }}>🔍 No results found.</p>
      )}

      {sortedResults.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, backgroundColor: 'var(--card-bg)' }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={{ ...th, whiteSpace: 'nowrap' }}>Actions</th>
              {fieldKeys.map((field, idx) => (
                <th
                  key={field}
                  style={{ ...th, cursor: 'pointer' }}
                  onClick={() => handleSort(field)}
                >
                  {fieldLabels[idx] || field}
                  {sortField === field && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
              <th style={th}>Matched Text</th>
            </tr>
          </thead>
          <tbody>
            {pageResults.map((r, i) => {
              const globalIndex = startIdx + i;
              const parsed = parseFilename(r.filename);
              const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const highlightedSnippet = r.snippet.replace(
                new RegExp(escapeRe(query), 'gi'),
                (match) => `<mark class=\"text-highlight\">${match}</mark>`
              );

              return (
                <tr
                  key={globalIndex}
                  style={globalIndex === selectedRow
                    ? { backgroundColor: 'var(--row-selected-bg)' }
                    : globalIndex % 2 === 0
                      ? { backgroundColor: 'var(--row-even-bg)' }
                      : { backgroundColor: 'var(--row-odd-bg)' }}
                  onClick={() => setSelectedRow(globalIndex)}
                >
                  <td style={td}>{globalIndex + 1}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center' }}>
                      <a
                        href={`${API_URL}/files/${encodeURIComponent(r.filename)}?token=${encodeURIComponent((localStorage.getItem('auth_token')||''))}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-view"
                      >
                        <FiEye />
                        <span style={{ marginLeft: 6 }}>View</span>
                      </a>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOutlineFile(r.filename);
                        }}
                        title="Open sortable document outline"
                      >
                        <FiList />
                        <span style={{ marginLeft: 6 }}>Outline</span>
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          if (onEmail) onEmail(r.filename);
                        }}
                        className="btn btn-primary"
                      >
                        <FiMail />
                        <span style={{ marginLeft: 6 }}>Email</span>
                      </button>
                    </div>
                  </td>
                  {fieldKeys.map((k) => (
                    <td key={k} style={td}>{parsed[k] || ''}</td>
                  ))}
                  <td style={td} className="matched-text" dangerouslySetInnerHTML={{ __html: highlightedSnippet }} />
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {sortedResults.length > 0 && (
        <div style={pagerRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="search-rpp" style={{ opacity: 0.85 }}>Rows per page:</label>
            <select
              id="search-rpp"
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Math.max(1, parseInt(e.target.value || '50', 10) || 50)); setPage(0); }}
              style={pagerSelect}
            >
              {[25, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div style={{ opacity: 0.9 }}>
            {sortedResults.length === 0
              ? 'No results'
              : `Showing ${startIdx + 1}–${endIdx} of ${sortedResults.length}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn" onClick={() => setPage(0)} disabled={page <= 0}>First</button>
            <button className="btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page <= 0}>Prev</button>
            <span style={{ minWidth: 110, textAlign: 'center' }}>Page {Math.min(page + 1, totalPages)} / {totalPages}</span>
            <button className="btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</button>
            <button className="btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</button>
          </div>
        </div>
      )}
      {outlineFile && (
        <PdfOutlineModal
          filename={outlineFile}
          API_URL={API_URL}
          onClose={() => setOutlineFile('')}
        />
      )}
    </div>
  );
};

// Inline styles
const th = {
  textAlign: 'left',
  padding: '12px 10px',
  backgroundColor: 'var(--table-head-bg)',
  color: 'var(--text)',
  borderBottom: '2px solid var(--border)',
  fontSize: '16px',
  fontWeight: 'bold'
};

const td = {
  padding: '10px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
};

const viewButton = {
  padding: '6px 12px',
  backgroundColor: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
};

export default SearchView;

// Pager styles
const pagerRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 12,
  marginTop: 8,
};

const pagerSelect = {
  padding: '6px 8px',
  background: 'var(--card-bg)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
};
